/**
 * Business Terms Page
 * 
 * Displays business terminology with full accessibility support
 * and consistent styling with other context pages.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CircularProgress, 
  Box, 
  Alert,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import { 
  Search as SearchIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
// Import auth context
import { useAuth } from '../contexts/AuthContext';
// Import business term service
import businessTermService, { BusinessTerm as APIBusinessTerm } from '../services/businessTermService';

// Inline implementation of useDebounce hook to avoid module resolution issues
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Sample business term type for demonstration
interface BusinessTerm {
  id: string;
  name: string;
  definition: string;
  domain: string;
  status: 'approved' | 'proposed' | 'draft' | 'deprecated';
  steward: string;
  lastUpdated: string;
  relatedTerms?: string[];
  tags?: string[];
  examples?: string[];
}

const BusinessTerms: React.FC = () => {
  // State management
  const [terms, setTerms] = useState<BusinessTerm[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [editingTermId, setEditingTermId] = useState<string | null>(null);
  const [editedTerm, setEditedTerm] = useState<BusinessTerm | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  
  // Auth context for user permissions
  const { user } = useAuth();

  // Handle edit mode
  const handleEditClick = (term: BusinessTerm, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTermId(term.id);
    setEditedTerm({ ...term });
    // Focus the first input field after state updates
    setTimeout(() => {
      nameInputRef.current?.focus();
    }, 100);
  };

  // Handle cancel edit
  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTermId(null);
    setEditedTerm(null);
  };

  // Handle save changes
  const handleSaveChanges = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editedTerm) return;

    try {
      // Call API to update term with all Unidatum fields
      await businessTermService.updateBusinessTerm(editedTerm.id, {
        name: editedTerm.name,
        definition: editedTerm.definition,
        category: 'Other', // Keep existing category
        privacyClassification: (editedTerm as any).privacyClassification,
        sensitivityLevel: (editedTerm as any).sensitivityLevel,
        dataElementLabel: (editedTerm as any).dataElementLabel,
        dataPrivacyImpact: (editedTerm as any).dataPrivacyImpact
      });

      // Update local state
      setTerms(prevTerms => 
        prevTerms.map(t => t.id === editedTerm.id ? editedTerm : t)
      );

      setSaveSuccess(`Successfully updated "${editedTerm.name}"`);
      setEditingTermId(null);
      setEditedTerm(null);
    } catch (err) {
      console.error('Failed to save changes:', err);
      setSaveError('Failed to save changes. Please try again.');
    }
  };

  // Handle field changes
  const handleFieldChange = (field: keyof BusinessTerm, value: any) => {
    if (editedTerm) {
      setEditedTerm({ ...editedTerm, [field]: value });
    }
  };

  // Handle keyboard navigation in edit mode
  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setEditingTermId(null);
      setEditedTerm(null);
    }
  };
  
  // Debounced search text to prevent excessive API calls
  const debouncedSearchText = useDebounce(searchText, 500);

  // Sample data - in a real app this would come from an API
  const sampleTerms: BusinessTerm[] = [
    {
      id: 'term-001',
      name: 'Customer',
      definition: 'An individual or organization that purchases products or services from our company.',
      domain: 'Customer Relationship Management',
      status: 'approved',
      steward: 'Jane Smith',
      lastUpdated: '2025-06-15',
      relatedTerms: ['Prospect', 'Client', 'Account'],
      tags: ['customer', 'crm', 'sales'],
      examples: ['John Doe purchasing a subscription', 'ACME Corp with enterprise license']
    },
    {
      id: 'term-002',
      name: 'Revenue',
      definition: 'Income generated from business activities, usually from the sale of goods and services.',
      domain: 'Finance',
      status: 'approved',
      steward: 'Michael Johnson',
      lastUpdated: '2025-06-20',
      relatedTerms: ['Income', 'Sales', 'Profit'],
      tags: ['finance', 'accounting', 'sales'],
      examples: ['Monthly subscription payments', 'One-time license fees']
    },
    {
      id: 'term-003',
      name: 'Data Governance',
      definition: 'The overall management of data availability, relevancy, usability, integrity and security in an enterprise.',
      domain: 'Data Management',
      status: 'approved',
      steward: 'Sarah Williams',
      lastUpdated: '2025-07-05',
      relatedTerms: ['Data Quality', 'Data Stewardship', 'Metadata Management'],
      tags: ['governance', 'data management', 'compliance'],
      examples: ['Data quality rules implementation', 'Data ownership assignment']
    },
    {
      id: 'term-004',
      name: 'Customer Lifetime Value',
      definition: 'The total worth of a customer over the entire relationship with the business.',
      domain: 'Marketing',
      status: 'approved',
      steward: 'Robert Davis',
      lastUpdated: '2025-07-12',
      relatedTerms: ['Customer Acquisition Cost', 'Churn Rate', 'Customer Retention'],
      tags: ['marketing', 'analytics', 'customer'],
      examples: ['$5,000 average CLV for premium tier', 'CLV calculation model']
    },
    {
      id: 'term-005',
      name: 'Personally Identifiable Information',
      definition: 'Information that can be used to identify, contact, or locate an individual person.',
      domain: 'Data Privacy',
      status: 'approved',
      steward: 'Emily Anderson',
      lastUpdated: '2025-07-25',
      relatedTerms: ['GDPR', 'Data Protection', 'Privacy Policy'],
      tags: ['privacy', 'compliance', 'security'],
      examples: ['Name, email address, social security number', 'Biometric data']
    },
    {
      id: 'term-006',
      name: 'Net Promoter Score',
      definition: 'A metric for assessing customer loyalty based on likelihood to recommend a company or product.',
      domain: 'Customer Experience',
      status: 'approved',
      steward: 'Thomas Wilson',
      lastUpdated: '2025-08-01',
      relatedTerms: ['Customer Satisfaction', 'Customer Feedback', 'Brand Loyalty'],
      tags: ['customer experience', 'metrics', 'satisfaction'],
      examples: ['Scale from 0-10', 'Promoters (9-10), Passives (7-8), Detractors (0-6)']
    }
  ];

  // Function to fetch business terms from API
  const fetchBusinessTerms = useCallback(async () => {
    // Only set loading true if we don't already have terms loaded (prevents flashing)
    if (terms.length === 0) {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Fetch from API using authenticated service with large limit to get all terms
      const response = await businessTermService.getBusinessTerms({ limit: 1000 });
      let apiTerms: APIBusinessTerm[] = response.data || [];
      
      console.log('API Response:', { 
        success: response.success, 
        count: response.count, 
        total: response.total, 
        dataLength: apiTerms.length 
      });
      
      // Transform API data to match local interface
      let transformedTerms: BusinessTerm[] = apiTerms.map((term: APIBusinessTerm) => ({
        id: term._id,
        name: term.name,
        definition: term.definition,
        domain: term.domain?.name || 'Uncategorized',
        status: term.status as 'approved' | 'proposed' | 'draft' | 'deprecated',
        steward: term.owner?.name || 'Unassigned',
        lastUpdated: term.updatedAt ? new Date(term.updatedAt).toISOString().split('T')[0] : 'N/A',
        relatedTerms: term.relatedTerms?.map(rt => rt.termId?.toString()) || [],
        tags: term.tags || [],
        examples: term.examples || []
      }));
      
      // Apply search filter if needed
      if (debouncedSearchText) {
        const searchLower = debouncedSearchText.toLowerCase();
        transformedTerms = transformedTerms.filter(term => 
          term.name.toLowerCase().includes(searchLower) ||
          term.definition.toLowerCase().includes(searchLower) ||
          term.domain.toLowerCase().includes(searchLower) ||
          term.steward.toLowerCase().includes(searchLower) ||
          (term.tags && term.tags.some(tag => tag.toLowerCase().includes(searchLower))) ||
          (term.relatedTerms && term.relatedTerms.some(related => related.toLowerCase().includes(searchLower)))
        );
        
        // Add to search history if it's a new search
        if (!searchHistory.includes(debouncedSearchText)) {
          const newHistory = [...searchHistory.slice(-4), debouncedSearchText];
          setSearchHistory(newHistory);
          localStorage.setItem('businessTermsSearchHistory', JSON.stringify(newHistory));
        }
      }
      
      console.log('Setting terms:', transformedTerms.length, 'total terms');
      setTerms(transformedTerms);
    } catch (err) {
      console.error('Failed to fetch business terms:', err);
      setError('Failed to load business terms. Please try again later.');
      // Fallback to sample data if API fails
      setTerms(sampleTerms);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchText, searchHistory]);

  // Load search history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('businessTermsSearchHistory');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        if (Array.isArray(parsed)) {
          setSearchHistory(parsed);
        }
      } catch (e) {
        console.error('Error parsing search history from localStorage:', e);
      }
    }
  }, []);

  // Fetch terms when debounced search text changes
  useEffect(() => {
    fetchBusinessTerms();
  }, [fetchBusinessTerms]);

  // Get status color based on status value
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return '#4CAF50'; // Green
      case 'proposed':
        return '#2196F3'; // Blue
      case 'draft':
        return '#FFC107'; // Yellow
      case 'deprecated':
        return '#9E9E9E'; // Gray
      default:
        return '#9E9E9E'; // Gray
    }
  };

  return (
    <Container sx={{ py: 4 }} className="business-terms-page">
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#003366', fontWeight: 700 }} tabIndex={-1}>
        Business Terminology
      </Typography>
      <Typography variant="body1" paragraph>
        Explore and manage standard business terms and definitions used across the organization.
      </Typography>

      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search business terms..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton 
                  aria-label="Clear search" 
                  onClick={() => setSearchText('')}
                  edge="end"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
          aria-label="Search business terms"
        />
        
        {/* Search history suggestions */}
        {searchHistory.length > 0 && !searchText && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Recent searches:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {searchHistory.map((term, index) => (
                <Chip 
                  key={`${term}-${index}`}
                  label={term} 
                  size="small"
                  onClick={() => setSearchText(term)}
                  clickable
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }} aria-live="polite">
          <CircularProgress aria-label="Loading business terms" />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Box sx={{ mt: 2, mb: 2 }} aria-live="assertive">
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Results Section */}
      {!loading && !error && (
        <>
          <Box sx={{ mb: 2 }} aria-live="polite">
            <Typography variant="subtitle1">
              {terms.length > 0 
                ? `Showing ${terms.length} business terms` 
                : 'No business terms found'}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {terms.map((term) => {
              const isEditing = editingTermId === term.id;
              const displayTerm = isEditing && editedTerm ? editedTerm : term;
              
              return (
              <Grid item xs={12} sm={6} md={4} key={term.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: isEditing ? 'default' : 'pointer',
                    '&:hover': isEditing ? {} : { boxShadow: 3 },
                    '&:focus-within': { boxShadow: 3, outline: '2px solid #1785FB' },
                    position: 'relative',
                    transition: 'box-shadow 0.3s, outline 0.3s',
                    backgroundColor: isEditing ? '#f5f5f5' : 'white'
                  }}
                  tabIndex={isEditing ? -1 : 0}
                  role="button"
                  aria-label={isEditing ? `Editing ${term.name}` : `Click to edit ${term.name}`}
                  onKeyDown={(e) => {
                    if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleEditClick(term, e as any);
                    }
                  }}
                  onClick={(e) => {
                    if (!isEditing) {
                      handleEditClick(term, e);
                    }
                  }}
                >
                  <CardContent onKeyDown={isEditing ? handleEditKeyDown : undefined}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1
                      }}
                    >
                      {isEditing ? (
                        <TextField
                          inputRef={nameInputRef}
                          fullWidth
                          label="Term Name"
                          value={displayTerm.name}
                          onChange={(e) => handleFieldChange('name', e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ mr: 1 }}
                          inputProps={{
                            'aria-label': 'Edit term name'
                          }}
                        />
                      ) : (
                        <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
                          {displayTerm.name}
                        </Typography>
                      )}
                      <Chip 
                        label={displayTerm.status} 
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(displayTerm.status),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </Box>
                    
                    {isEditing ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Definition"
                        value={displayTerm.definition}
                        onChange={(e) => handleFieldChange('definition', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ mt: 1, mb: 2 }}
                        inputProps={{
                          'aria-label': 'Edit definition'
                        }}
                      />
                    ) : (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mt: 1, 
                          mb: 2,
                          color: 'text.secondary'
                        }}
                      >
                        {displayTerm.definition}
                      </Typography>
                    )}
                    
                    {/* Unidatum-Standard Fields (Edit Mode Only) */}
                    {isEditing && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#003366', fontWeight: 600 }}>
                          Privacy & Classification
                        </Typography>
                        
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Privacy Classification</InputLabel>
                              <Select
                                value={(editedTerm as any)?.privacyClassification || 'Internal'}
                                label="Privacy Classification"
                                onChange={(e) => handleFieldChange('privacyClassification' as any, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MenuItem value="Public">Public</MenuItem>
                                <MenuItem value="Internal">Internal</MenuItem>
                                <MenuItem value="Confidential">Confidential</MenuItem>
                                <MenuItem value="Highly Confidential">Highly Confidential</MenuItem>
                                <MenuItem value="PII">PII (Personally Identifiable)</MenuItem>
                                <MenuItem value="PHI">PHI (Protected Health Info)</MenuItem>
                                <MenuItem value="PCI">PCI (Payment Card Info)</MenuItem>
                                <MenuItem value="Restricted">Restricted</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Sensitivity Level</InputLabel>
                              <Select
                                value={(editedTerm as any)?.sensitivityLevel || 'Medium'}
                                label="Sensitivity Level"
                                onChange={(e) => handleFieldChange('sensitivityLevel' as any, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MenuItem value="Low">Low</MenuItem>
                                <MenuItem value="Medium">Medium</MenuItem>
                                <MenuItem value="High">High</MenuItem>
                                <MenuItem value="Critical">Critical</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Data Element Label"
                              value={(editedTerm as any)?.dataElementLabel || ''}
                              onChange={(e) => handleFieldChange('dataElementLabel' as any, e.target.value.toUpperCase())}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="e.g., CUST_FIRST_NAME"
                              helperText="Standardized uppercase label"
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Retention Period"
                              value={(editedTerm as any)?.dataPrivacyImpact?.retentionPeriod || ''}
                              onChange={(e) => handleFieldChange('dataPrivacyImpact' as any, {
                                ...(editedTerm as any)?.dataPrivacyImpact,
                                retentionPeriod: e.target.value
                              })}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="e.g., 7 years, indefinite"
                            />
                          </Grid>
                        </Grid>
                        
                        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                          Compliance Flags:
                        </Typography>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid item xs={6} sm={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={(editedTerm as any)?.dataPrivacyImpact?.gdprRelevant || false}
                                  onChange={(e) => handleFieldChange('dataPrivacyImpact' as any, {
                                    ...(editedTerm as any)?.dataPrivacyImpact,
                                    gdprRelevant: e.target.checked
                                  })}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label="GDPR"
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={(editedTerm as any)?.dataPrivacyImpact?.ccpaRelevant || false}
                                  onChange={(e) => handleFieldChange('dataPrivacyImpact' as any, {
                                    ...(editedTerm as any)?.dataPrivacyImpact,
                                    ccpaRelevant: e.target.checked
                                  })}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label="CCPA"
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={(editedTerm as any)?.dataPrivacyImpact?.hipaaRelevant || false}
                                  onChange={(e) => handleFieldChange('dataPrivacyImpact' as any, {
                                    ...(editedTerm as any)?.dataPrivacyImpact,
                                    hipaaRelevant: e.target.checked
                                  })}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label="HIPAA"
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={(editedTerm as any)?.dataPrivacyImpact?.pciRelevant || false}
                                  onChange={(e) => handleFieldChange('dataPrivacyImpact' as any, {
                                    ...(editedTerm as any)?.dataPrivacyImpact,
                                    pciRelevant: e.target.checked
                                  })}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label="PCI"
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                    
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                      <strong>Domain:</strong> {displayTerm.domain}
                    </Typography>
                    
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                      <strong>Steward:</strong> {displayTerm.steward}
                    </Typography>
                    
                    <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                      <strong>Last Updated:</strong> {displayTerm.lastUpdated}
                    </Typography>

                    {/* Edit/Save/Cancel Buttons */}
                    {isEditing && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={handleSaveChanges}
                          size="small"
                          aria-label="Save changes"
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<CancelIcon />}
                          onClick={handleCancelEdit}
                          size="small"
                          aria-label="Cancel editing"
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}

                    {!isEditing && displayTerm.examples && displayTerm.examples.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          Examples:
                        </Typography>
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                          {displayTerm.examples.map((example, index) => (
                            <li key={index}>
                              <Typography variant="caption">{example}</Typography>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    )}
                    
                    {!isEditing && displayTerm.relatedTerms && displayTerm.relatedTerms.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                          Related Terms:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {displayTerm.relatedTerms.map(relatedTerm => (
                            <Chip 
                              key={relatedTerm} 
                              label={relatedTerm} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchText(relatedTerm);
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {!isEditing && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {displayTerm.tags?.map(tag => (
                        <Chip 
                          key={tag} 
                          label={tag} 
                          size="small" 
                          variant="outlined"
                        />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!saveSuccess}
        autoHideDuration={4000}
        onClose={() => setSaveSuccess('')}
        message={saveSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Error Snackbar */}
      <Snackbar
        open={!!saveError}
        autoHideDuration={6000}
        onClose={() => setSaveError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSaveError('')}>
          {saveError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BusinessTerms;
